import React from 'react';
import Layout from '../components/Layout.jsx';

function CertificateIcon({ className = '' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" />
    </svg>
  );
}

function CertificateCard({ cert }) {
  return (
    <div className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-indigo-300 hover:shadow-md transition">
      <div className="h-2 bg-gradient-to-r from-indigo-500 to-indigo-600" />
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <CertificateIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{cert.title}</h3>
            {cert.issued_at && (
              <p className="text-xs text-gray-500 mt-1">Issued {cert.issued_at}</p>
            )}
          </div>
        </div>
        <div className="mt-4">
          {cert.download_url ? (
            <a
              href={`/certificates/${encodeURIComponent(cert.id)}/view`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-800"
            >
              View certificate
              <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
          ) : (
            <span className="text-xs text-gray-400">Certificate file unavailable</span>
          )}
        </div>
      </div>
    </div>
  );
}

function WebinarCertificateCard({ cert }) {
  return (
    <div className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-indigo-300 hover:shadow-md transition">
      <div className="h-2 bg-gradient-to-r from-emerald-500 to-emerald-600" />
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <CertificateIcon className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2">{cert.title}</h3>
            <p className="text-xs text-gray-500 mt-1">
              Live webinar
              {cert.completed_on ? ` · ${cert.completed_on}` : ''}
              {cert.credit_hours ? ` · ${cert.credit_hours} credits` : ''}
            </p>
            {cert.certificate_number && (
              <p className="text-xs text-gray-400 mt-0.5">ID {cert.certificate_number}</p>
            )}
          </div>
        </div>
        <div className="mt-4">
          <a
            href={cert.download_url}
            className="inline-flex items-center text-sm font-medium text-emerald-700 hover:text-emerald-900"
          >
            Download certificate
            <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Certificates({ certificates = [], error, webinar_certificates: webinarCertificates = [] }) {
  const count = certificates.length + webinarCertificates.length;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Certificates</h1>
            <p className="text-sm text-gray-500 mt-1">Your earned certificates of completion.</p>
          </div>
          {count > 0 && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 text-sm font-medium rounded-full">
              <CertificateIcon className="w-4 h-4" />
              {count} {count === 1 ? 'certificate' : 'certificates'}
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 text-sm">{error}</div>
        )}

        {webinarCertificates.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {webinarCertificates.map((cert) => (
              <WebinarCertificateCard key={`webinar-${cert.id}`} cert={cert} />
            ))}
          </div>
        )}

        {!error && count === 0 && (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center">
              <CertificateIcon className="w-7 h-7" />
            </div>
            <p className="mt-4 text-gray-700 font-medium">No certificates yet</p>
            <p className="text-sm text-gray-500 mt-1">Complete a course and your certificate will appear here.</p>
          </div>
        )}

        {certificates.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {certificates.map((cert) => (
              <CertificateCard key={cert.id} cert={cert} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
